import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  NgModule,
  ErrorHandler,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ScaffoldModule } from './scaffold/scaffold.module';
import { LoginComponent } from './account/login/login.component';
import { CookieService } from 'ngx-cookie-service';
import { MomentModule } from 'ngx-moment';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { SharedModule } from '@app/shared/shared.module';
import { ResetPasswordComponent } from './account/reset-password/reset-password.component';
import { MustHaveUpercaseDirective } from './validators/must-have-upercase.directive';
import { MustHaveLowercaseDirective } from './validators/must-have-lowercase.directive';
import { MustHaveNumberDirective } from './validators/must-have-number.directive';
import { MustContainCharactersDirective } from './validators/must-contain-characters.directive';
import { MustMatchInputDirective } from './validators/must-match-input.directive';
import { ForgotPasswordComponent } from './account/forgot-password/forgot-password.component';
import { NewPasswordRequiredComponent } from './account/new-password-required/new-password-required.component';
import { ModelTypeListComponent } from './data-model/type-list/type-list.component';
import { ModelKeysComponent } from './data-model/model-keys/model-keys.component';
import { ConfirmDialog } from './generic/confirm-dialog/confirm-dialog.component';
import { NewDataModelComponent } from './data-model/new-data-model/new-data-model.component';
import { ModelDetailsComponent } from './data-model/model-details/model-details.component';
import { ModelModesComponent } from './data-model/model-modes/model-modes.component';
import { KeyItemsComponent } from './data-model/key-items/key-items.component';
import { MinValueDirective } from './validators/min-value.directive';
import { MaxValueDirective } from './validators/max-value.directive';
import { VerifyPhoneAndEmailComponent } from './account/verify-phone-and-email/verify-phone-and-email.component';
import { ValidUrlDirective } from './validators/valid-url.directive';
import { NoWhiteSpaceDirective } from './validators/no-white-space.directive';
import { DataModelPrefsComponent } from './data-model/data-model-prefs/data-model-prefs.component';
import { DataConfigUpdateComponent } from './data-model/data-config-update/data-config-update.component';
import { CatConfigComponent } from './data-model/cat-config/cat-config.component';
import { NewDataCategoryComponent } from './data-model/new-data-category/new-data-category.component';
import { TypeConfigComponent } from './data-model/type-config/type-config.component';
import { PendingChangesGuard } from '@app/shared/pending-changes.guard';
import { SaveDialogComponent } from './generic/save-dialog/save-dialog.component';
import { LtiCallbackComponent } from './utility/lti-callback/lti-callback.component';
import { LtiFrameComponent } from './utility/lti-frame/lti-frame.component';
import { NewUserEmailComponent } from './admin/new-user-email/new-user-email.component';
// import { ChangelogComponent } from './changes/changelog/changelog.component';
import { CommentSidebarComponent } from './commenting/comment-sidebar/comment-sidebar.component';
import { NewDataComponent } from '@scaffold/routes/new-data/new-data.component';
import { SecurityComponent } from './security/security.component';
import { TypeSelectComponent } from './account/type-select/type-select.component';

import { translations } from '@zxcvbn-ts/language-en';
import { provideZxvbnServiceForPSM } from './framework/password-strength-meter/default-provider';
import { ZxvbnConfigType } from './framework/password-strength-meter/password-strength-meter.types';
import { PasswordStrengthMeterModule } from './framework/password-strength-meter/password-strength-meter.module';
import './utility/update-password-matchers';
import { QrCodeComponent } from './generic/qr-code/qr-code.component';
import { MfaModalComponent } from './account/mfa-modal/mfa-modal.component';
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import { Observable, of, catchError } from 'rxjs';
import * as enUSTranslations from '../assets/i18n/en-US.json';
import { EnvironmentService } from '@services/environment.service';
import { OverlayComponent } from './generic/overlay/overlay.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { ChoiceDialogComponent } from './generic/choice-dialog/choice-dialog.component';
import { GenericModule } from '@generic/generic.module';
import { NumberDirective } from './validators/number.directive';
import { AngularSplitModule } from 'angular-split';
import { PrimengModule } from './shared/primeng.module';
import customPreset from './shared/custom-preset';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

const zxvbnConfig: ZxvbnConfigType = {
  translations: translations,
};

const staticEnUSTranslations =
  (enUSTranslations as any).default || enUSTranslations;

class FallbackTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}

  getTranslation(lang: string): Observable<any> {
    if (lang === 'en-US') {
      return of(staticEnUSTranslations);
    }
    return this.http
      .get(`assets/i18n/${lang}.json`)
      .pipe(catchError(() => of(staticEnUSTranslations)));
  }
}

function initializeTranslations(translate: TranslateService) {
  return () => {
    translate.setTranslation('en-US', staticEnUSTranslations);
    translate.setDefaultLang('en-US');
    translate.use('en-US');
  };
}
@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ResetPasswordComponent,
    MustHaveUpercaseDirective,
    MustHaveLowercaseDirective,
    MustHaveNumberDirective,
    MustContainCharactersDirective,
    MustMatchInputDirective,
    ForgotPasswordComponent,
    NewPasswordRequiredComponent,
    ModelTypeListComponent,
    ModelKeysComponent,
    ConfirmDialog,
    NewDataModelComponent,
    ModelDetailsComponent,
    ModelModesComponent,
    NewDataComponent,
    KeyItemsComponent,
    MinValueDirective,
    MaxValueDirective,
    VerifyPhoneAndEmailComponent,
    ValidUrlDirective,
    NoWhiteSpaceDirective,
    DataModelPrefsComponent,
    CatConfigComponent,
    NewDataCategoryComponent,
    TypeConfigComponent,
    SaveDialogComponent,
    LtiCallbackComponent,
    NewUserEmailComponent,
    LtiFrameComponent,
    // ChangelogComponent,
    CommentSidebarComponent,
    SecurityComponent,
    TypeSelectComponent,
    DataConfigUpdateComponent,
    QrCodeComponent,
    MfaModalComponent,
    OverlayComponent,
    NumberDirective,
    ChoiceDialogComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    PrimengModule,
    AngularSplitModule,
    GenericModule,
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ScaffoldModule,
    MomentModule,
    SharedModule,
    ReactiveFormsModule,
    PasswordStrengthMeterModule,
    // LoadingBarHttpClientModule,
    // LoadingBarModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: 'en-US',
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => new FallbackTranslateLoader(http),
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    provideAppInitializer(() => {
      initializeTranslations(inject(TranslateService))();
    }),
    CookieService,
    PendingChangesGuard,
    provideZxvbnServiceForPSM(zxvbnConfig),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: customPreset,
        options: {
          cssLayer: { name: 'primeng', order: 'primeng, app-styles' },
          darkModeSelector: '.dark-theme',
        },
      },
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ],
})
export class AppModule {}
